/** Wire types shared by the web client and the Lambda handler. */

import { MAX_BALLS } from './balls.js';

export interface GameMeta {
  slug: string;
  title: string;
  /** Object keys, smallest ball first. Length is the game's ball count. */
  balls: string[];
  createdAt: string;
  /**
   * True when editing the roster needs the passphrase. The passphrase itself never
   * leaves the API — the client only ever learns whether there is one.
   */
  hasPassphrase?: boolean;
}

export interface ScoreEntry {
  name: string;
  score: number;
  at: string;
}

// --- GET /api/games/{slug}/available ---

export interface SlugAvailability {
  slug: string;
  available: boolean;
  /** Present when unavailable: 'taken' or one of the SlugProblem values. */
  reason?: string;
  /** A free alternative, offered when the requested slug is taken. */
  suggestion?: string;
}

// --- POST /api/games/init ---

export interface InitGameRequest {
  slug: string;
  title: string;
  ballCount: number;
  /** Empty or absent leaves the roster open for anyone with the link to edit. */
  passphrase?: string;
}

export interface UploadSlot {
  index: number;
  key: string;
  /** Where to PUT the image. Presigned in production, a local route in dev. */
  url: string;
  contentType: string;
}

export interface InitGameResponse {
  slug: string;
  /** Proves ownership of the reservation when finalizing. */
  token: string;
  uploads: UploadSlot[];
}

// --- POST /api/games/{slug}/finalize ---

export interface FinalizeGameRequest {
  token: string;
}

// --- Editing a published roster ---

/** POST /api/games/{slug}/edit/check — unlocks the edit screen, changes nothing. */
export interface CheckPassphraseRequest {
  passphrase: string;
}

export interface CheckPassphraseResponse {
  ok: boolean;
}

/** POST /api/games/{slug}/edit/init — somewhere to put the photos being added. */
export interface EditInitRequest {
  passphrase?: string;
  /** How many brand-new photos this edit adds. Zero is fine: a pure reorder. */
  newBallCount: number;
}

export interface EditInitResponse {
  /** Tags this edit's uploads, keeping them off any key a CDN already cached. */
  rev: string;
  uploads: UploadSlot[];
}

/** POST /api/games/{slug}/edit/save — replaces the roster wholesale. */
export interface EditSaveRequest {
  passphrase?: string;
  /** The finished roster, smallest ball first: kept keys and freshly uploaded ones. */
  balls: string[];
}

// --- Scores ---

export interface SubmitScoreRequest {
  name: string;
  score: number;
}

export interface ScoreListResponse {
  scores: ScoreEntry[];
}

export interface ApiError {
  error: string;
  message: string;
}

export const MAX_UPLOAD_BYTES = 1_000_000;
export const BALL_IMAGE_CONTENT_TYPE = 'image/webp';
export const MAX_TITLE_LENGTH = 60;
export const MAX_PLAYER_NAME_LENGTH = 20;
export const MAX_PASSPHRASE_LENGTH = 64;
export const MAX_SCORE = 10_000_000;
export const LEADERBOARD_SIZE = 10;

/** Where a game's ball images live, relative to the site root. */
export function ballKey(slug: string, index: number): string {
  return `u/${slug}/${index}.webp`;
}

/** Hex characters in the revision tag an edit stamps on its uploads. */
export const EDIT_REV_LENGTH = 8;

/**
 * Where the photos added by an edit go.
 *
 * A new key rather than the old one, deliberately. Ball images are served with a
 * year-long immutable cache, so overwriting `u/slug/3.webp` would leave players
 * looking at the previous photo — in their browser and at the CDN edge — long after
 * the roster changed. Every edit writes keys nothing has ever fetched.
 */
export function editBallKey(slug: string, rev: string, index: number): string {
  return `u/${slug}/${rev}-${index}.webp`;
}

/** Both key shapes above, and nothing else. */
const BALL_KEY_PATTERN = new RegExp(
  `^u/([a-z0-9-]+)/(?:[a-f0-9]{${EDIT_REV_LENGTH}}-)?(\\d{1,2})\\.webp$`,
);

/** True if `key` is a ball image belonging to `slug`. Guards what a roster may point at. */
export function isBallKeyFor(slug: string, key: string): boolean {
  const match = BALL_KEY_PATTERN.exec(key);
  return match?.[1] === slug && Number(match[2]) < MAX_BALLS;
}
